<script setup lang="ts">
import type { VueInstance } from '@vueuse/core'
import { getCurrentInstance } from 'vue'
import {
  Field,
  ErrorMessage,
  validate,
  useFieldError,
  useIsFieldDirty,
} from 'vee-validate'
import Icon from '~/components/common/Icon.vue'
import { twMerge } from 'tailwind-merge'
import { useClipboard } from '@vueuse/core'
import kebabCase from 'lodash/kebabCase'

const borderColors = {
  success: 'focus-within:!ring-good valid:!border-good !border-good',
  error: 'focus-within:!ring-error valid:!border-error !border-error',
  neutral: 'focus-within:!ring-p-500 !border-s-500',
}

const props = withDefaults(
  defineProps<{
    label?: string
    type?: string
    placeholder?: string
    isFocused?: boolean
    name?: string
    rules?: ComputedRef<string> | string
    required?: boolean
    validateOnBlur?: boolean
    modelValue?: string
    defaultValue?: string
    withValidationColor?: boolean
    borderClassesInput?: string
    inputClasses?: string
    autocomplete?: string
    labelClasses?: string

    enableCopyToClipboard?: boolean
  }>(),
  {
    label: '',
    type: 'text',
    placeholder: '',
    isFocused: false,
    name: '',
    rules: '',
    required: false,
    validateOnBlur: true,
    modelValue: '',
    defaultValue: '',
    withValidationColor: false,
    borderClassesInput: '',
    inputClasses: '',
    autocomplete: 'off',
  },
)

const emit = defineEmits(['update:modelValue'])

const error = useFieldError(props.name)
const isDirty = useIsFieldDirty(props.name)
const { copy, copied } = useClipboard()

const _type = ref(props.type)
const field = ref<VueInstance>()
const instance = getCurrentInstance()
const id = `input-${instance.uid}`

const value = computed({
  get: () => props.modelValue,
  set: (newValue) => emit('update:modelValue', newValue),
})

const rulesField = toRef(props.rules) as ComputedRef<string>
const formInputTestId = computed(() => kebabCase(`form-input-${props.label}`))

const toggleType = () => {
  if (_type.value === 'text') {
    _type.value = 'password'
    return
  }
  _type.value = 'text'
}

const visibleType = computed(() => _type.value === 'text')

const isEmail = computed(
  () => props.type === 'email' || /email/.exec(rulesField.value ?? ''),
)

watch(value, async (newValue) => {
  if (!isEmail.value) return

  const { valid } = await validate(newValue, rulesField.value)

  if (valid && field.value?.$el) {
    field.value.$el.dispatchEvent(new Event('blur'))
  }
})

const borderClasses = ref('focus-within:ring-p-500 border-s-500')

watch(
  [() => props.withValidationColor, error, isDirty],
  ([withValidationColor, error]) => {
    if (!withValidationColor) {
      borderClasses.value = borderColors.neutral
      return
    }

    if (error) {
      borderClasses.value = borderColors.error
      return
    }

    borderClasses.value = borderColors.success
  },
)

onMounted(() => {
  if (props.isFocused) field.value.$el.focus()

  if (props.defaultValue) {
    value.value = props.defaultValue
  }
})
</script>

<template>
  <div
    :data-testid="formInputTestId"
    :class="[_type !== 'hidden' ? '' : 'hidden']"
  >
    <label
      :for="id"
      :class="twMerge('mb-2 text-xs font-semibold', labelClasses)"
    >
      {{ label }} <span v-if="required" class="text-error">*</span>
    </label>
    <div
      :class="[
        twMerge(
          'form-input relative flex w-full items-center gap-x-2 overflow-hidden rounded border border-s-600 p-0 transition-colors focus-within:border-p-500',
          borderClasses,
          borderClassesInput,
        ),
      ]"
    >
      <Field
        :id="id"
        ref="field"
        v-model="value"
        :name="name"
        :rules="rulesField"
        :class="[
          twMerge(
            'w-full border-none !outline-0 focus:ring-0 rounded !shadow-none text-grey-900 border-transparent py-1',
            inputClasses,
          ),
        ]"
        :autocomplete="autocomplete"
        :type="_type"
        :placeholder="placeholder"
        :validate-on-blur="validateOnBlur"
        :validate-on-input="!isEmail"
        :label="label"
      />

      <button
        v-if="enableCopyToClipboard"
        data-testid="copy-to-clipboard-button"
        class="w-7 h-7 flex items-center justify-center rounded"
        :class="{
          'bg-p-50 border-[1.5px] border-p-700': copied,
        }"
        @click="copy(value)"
      >
        <Icon class="text-s-900" name="icon-copy-alt" />
      </button>

      <!-- Password Toggle -->
      <div
        v-if="type === 'password'"
        class="flex size-7 cursor-pointer items-center text-s-900"
        @click="toggleType"
      >
        <Icon v-if="visibleType" name="icon-eye-off" />
        <Icon v-else name="icon-eye-on" />
      </div>

      <slot name="icon-right" />
    </div>

    <ErrorMessage class="block text-xxs text-error" :name="name" />
  </div>
</template>

<style>
input:-webkit-autofill,
input:-webkit-autofill:focus {
  transition:
    background-color 600000s 0s,
    color 600000s 0s;
}

input[data-autocompleted] {
  background-color: transparent !important;
}
</style>
